import brandKitDataSources from "./brandKitToDataSources";
import {
  fontSizeToLineHeight,
  type StyleRecord,
} from "./deriveProductGridStyling";

const placements = ["hero", "content"] as const;
type Placement = (typeof placements)[number];

// oc key -> generic styling dsKey it inherits from while unset. The chip is
// raw inline HTML inside mj-text, so it borrows the same generic slots the
// surrounding text and dividers wear: an un-customized chip follows the brand
// instead of the sample brand book's maroon Times New Roman.
export const OC_INHERITED_FALLBACKS: Record<string, string> =
  Object.fromEntries(
    placements.flatMap((placement) => [
      [`oc_${placement}_color`, "text_dark"],
      [`oc_${placement}_border_color`, "outline"],
      [`oc_${placement}_font_family`, "body_font_family"],
      // No generic body weight slot exists; headline_font_weight is the only
      // general-purpose one (the rest are button/link/alert specific) and it
      // already defaults to the chip's bold.
      [`oc_${placement}_font_weight`, "headline_font_weight"],
      [`oc_${placement}_font_size`, "font_size_lg"],
    ]),
  );

// oc keys whose placeholder must come from derivation rather than the field's
// own defaultValue: everything inherited above, plus the computed
// line-heights. The Components step reads this to show what a blank field
// currently resolves to.
export const OC_INHERITED_DISPLAY_KEYS: string[] = [
  ...Object.keys(OC_INHERITED_FALLBACKS),
  ...placements.map((placement) => `oc_${placement}_line_height`),
];

// Static `.bk-*-offer-style-1` defaults (FOG-1962) for the keys with no
// generic counterpart: an unset ("") field falls back to the sample the brand
// book ships with. Hero and content differ only in border widths and letter
// spacing. Border width is per-side and radius per-corner (user feedback on
// the first cut).
const chipDefaults = (placement: Placement): Record<string, string> => {
  const borderWidth = placement === "hero" ? "2px" : "1px";
  return {
    [`oc_${placement}_background_color`]: "transparent",
    [`oc_${placement}_padding`]: "3px 10px 3px 10px",
    [`oc_${placement}_border_style`]: "solid",
    [`oc_${placement}_border_top_width`]: borderWidth,
    [`oc_${placement}_border_right_width`]: borderWidth,
    [`oc_${placement}_border_bottom_width`]: borderWidth,
    [`oc_${placement}_border_left_width`]: borderWidth,
    [`oc_${placement}_border_radius_top_left`]: "0px",
    [`oc_${placement}_border_radius_top_right`]: "0px",
    [`oc_${placement}_border_radius_bottom_right`]: "0px",
    [`oc_${placement}_border_radius_bottom_left`]: "0px",
    [`oc_${placement}_font_style`]: "normal",
    [`oc_${placement}_letter_spacing`]: placement === "hero" ? "2px" : "normal",
    [`oc_${placement}_text_decoration`]: "none",
    [`oc_${placement}_text_transform`]: "uppercase",
  };
};

/** Brand-kit CSS classes the offer chip wears, per placement. */
export const OFFER_CHIP_CLASSES = {
  hero: "bk-hero-offer-style-1",
  content: "bk-content-offer-style-1",
} as const;

const buildChipCss = (
  placement: Placement,
  resolved: Record<string, string>,
): string => {
  const v = (suffix: string) => resolved[`oc_${placement}_${suffix}`];
  return (
    `.${OFFER_CHIP_CLASSES[placement]} {color:${v("color")};` +
    `background-color:${v("background_color")};padding:${v("padding")};` +
    `border-style:${v("border_style")};border-color:${v("border_color")};` +
    `border-width:${v("border_top_width")} ${v("border_right_width")} ${v("border_bottom_width")} ${v("border_left_width")};` +
    `border-radius:${v("border_radius_top_left")} ${v("border_radius_top_right")} ${v("border_radius_bottom_right")} ${v("border_radius_bottom_left")};` +
    `font-family:${v("font_family")};font-size:${v("font_size")};` +
    `font-style:${v("font_style")};font-weight:${v("font_weight")};` +
    `line-height:${v("line_height")};letter-spacing:${v("letter_spacing")};` +
    `text-decoration:${v("text_decoration")};text-transform:${v("text_transform")}}`
  );
};

const isSet = (value: string | undefined): value is string =>
  value != null && value !== "";

// Covers every generic dsKey, so an unhydrated styling record still resolves
// every inherited oc value instead of leaking undefined into the rule string.
const placeholderStyle = brandKitDataSources.style as StyleRecord;

/**
 * Resolves the offer-code (oc_*) styling record. Persisted values are kept
 * when non-empty (customized); empty/missing values are filled from the
 * generic styling counterpart (OC_INHERITED_FALLBACKS) or the static style-1
 * default. The output never contains "" — the mj-head emits the combined rule
 * strings unconditionally, so every part must hold a concrete value.
 *
 * The oc_{placement}_styles keys are the full CSS rules the mj-head injects;
 * they are derived-only by design (no form field exists for them) and always
 * rebuilt from the resolved parts.
 *
 * Pure and idempotent: derive(generic, derive(generic, persisted)) equals
 * derive(generic, persisted).
 */
export function deriveOfferCodeStyling(
  generic: StyleRecord,
  persisted: StyleRecord,
): Record<string, string> {
  const resolved: Record<string, string> = {};

  // Customized values win; extra persisted keys pass through untouched.
  Object.entries(persisted).forEach(([key, value]) => {
    if (isSet(value)) resolved[key] = value;
  });

  Object.entries(OC_INHERITED_FALLBACKS).forEach(([key, genericKey]) => {
    const fallback = generic[genericKey] || placeholderStyle[genericKey];
    if (!isSet(resolved[key]) && isSet(fallback)) resolved[key] = fallback;
  });

  placements.forEach((placement) => {
    Object.entries(chipDefaults(placement)).forEach(([key, fallback]) => {
      if (!isSet(resolved[key])) resolved[key] = fallback;
    });
  });

  // Line-height has a form field of its own, so a customization wins; left
  // blank it follows the resolved font size through the same rule every
  // generic size field applies.
  placements.forEach((placement) => {
    const key = `oc_${placement}_line_height`;
    if (!isSet(resolved[key])) {
      resolved[key] = fontSizeToLineHeight(
        resolved[`oc_${placement}_font_size`],
      );
    }
  });

  placements.forEach((placement) => {
    resolved[`oc_${placement}_styles`] = buildChipCss(placement, resolved);
  });

  return resolved;
}
